import { faLock } from '@fortawesome/pro-solid-svg-icons';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import { formatDateForAPI } from '@supply-mover-2-0/core';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { DatePicker } from '../forms/DatePicker';
import { FormField } from '../forms/FormField';
import { MultiSelect } from '../forms/MultiSelect';
import { Select } from '../forms/Select';
import { BaseDateRange } from './BaseDateRange';
import { BaseMultiSelect } from './BaseMultiSelect';
import { BaseNumberRange } from './BaseNumberRange';
import { BaseProximityFilter } from './BaseProximityFilter';
import { BaseRadioSelect } from './BaseRadioSelect';
import { BaseTextInput } from './BaseTextInput';

interface BaseFilterProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	/** When set, restricts the dropdown to only these values (for bounded filters) */
	boundedValues?: FilterOption[];
	layout?: 'sidebar' | 'tray' | 'inline' | 'bar';
	otherFilters?: Record<string, FilterOption[]>;
	isPreset?: boolean; // New prop to indicate if values are preset
}

/**
 * Universal Filter Component
 *
 * This component automatically renders the appropriate filter type
 * based on the configuration. It acts as a wrapper around the
 * specific filter implementations.
 */
export const BaseFilter: React.FC<BaseFilterProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	lockedValues = [],
	boundedValues,
	layout = 'inline',
	otherFilters = {},
	isPreset = false,
}) => {
	// Hoisted above the validation guards below. Those guards return early, so a
	// hook called after them runs a different number of times per render, which
	// is the rules-of-hooks violation React warns about — and the error markup
	// they render needs the token vocabulary anyway.
	const colors = useThemeColors();

	try {
		// Validate required props
		if (!filterType) {
			console.error('[BaseFilter] Missing filterType');
			return (
				<div
					className={cn(
						'p-2 text-danger text-xs',
						colors.classes.calloutDanger,
					)}
				>
					Error: Missing filter type
				</div>
			);
		}

		if (!config) {
			console.error('[BaseFilter] Missing config for filterType:', filterType);
			return (
				<div
					className={cn(
						'p-2 text-danger text-xs',
						colors.classes.calloutDanger,
					)}
				>
					Error: Missing filter configuration for {filterType}
				</div>
			);
		}

		if (!Array.isArray(selectedValues)) {
			console.error(
				'[BaseFilter] selectedValues is not an array:',
				selectedValues,
			);
			return (
				<div
					className={cn(
						'p-2 text-danger text-xs',
						colors.classes.calloutDanger,
					)}
				>
					Error: Invalid selectedValues for {filterType}
				</div>
			);
		}

		if (!Array.isArray(lockedValues)) {
			console.error('[BaseFilter] lockedValues is not an array:', lockedValues);
			return (
				<div
					className={cn(
						'p-2 text-danger text-xs',
						colors.classes.calloutDanger,
					)}
				>
					Error: Invalid lockedValues for {filterType}
				</div>
			);
		}
	} catch (error) {
		console.error(
			'[BaseFilter] Error during initialization validation:',
			error,
		);
		console.error('[BaseFilter] Props:', {
			filterType,
			config,
			selectedValues,
			lockedValues,
			disabled,
			isModalOpen,
			layout,
		});
		return (
			<div
				className={cn('p-2 text-danger text-xs', colors.classes.calloutDanger)}
			>
				Error: BaseFilter initialization failed -{' '}
				{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	try {
		const { t } = useTranslation();

		/**
		 * Every filter shows its label above the field — in the tray AND the
		 * header bar (owner decisions 2026-08-03; the earlier placeholder-only
		 * treatment read as half-finished once the tray was labelled). The
		 * label is also in the markup for assistive tech.
		 */
		const labelHidden = false;

		// Determine size based on layout
		// Bar controls sit one size below the header row above them —
		// subordinate to the tabs and page actions, matching the Save Search
		// and Filters buttons' size (owner calls 2026-08-03).
		const size =
			layout === 'sidebar' || layout === 'tray' || layout === 'bar'
				? 'xs'
				: 'md';

		switch (config.type) {
			case 'multi-select':
				// Use BaseMultiSelect for filters with API endpoints, MultiSelect for static options
				if (config.apiEndpoint) {
					return (
						<BaseMultiSelect
							filterType={filterType}
							config={config}
							selectedValues={selectedValues}
							onValuesChange={onValuesChange}
							disabled={disabled}
							isModalOpen={isModalOpen}
							lockedValues={lockedValues}
							boundedValues={boundedValues}
							size={size}
							layout={layout}
						/>
					);
				} else {
					return (
						<FormField
							name={filterType}
							label={t(config.label)}
							labelHidden={labelHidden}
							required={config.required}
							size={size}
						>
							<MultiSelect
								singleLine={layout === 'bar'}
								name={filterType}
								options={
									config.options?.map((opt) => ({
										value: opt.id?.toString() || String(opt.id),
										label: t(opt.label || String(opt.id)),
										subtitle: opt.subtitle,
										count: opt.count,
									})) || []
								}
								value={selectedValues.map((v) => {
									const id = v.id !== undefined ? v.id : v;
									const idString = id?.toString() || String(id);
									return {
										value: idString,
										label: t(v.label || idString),
										isFixed: lockedValues.some((locked) => {
											const lockedId =
												locked.id !== undefined ? locked.id : locked;
											const lockedIdString =
												lockedId?.toString() || String(lockedId);
											return lockedIdString === idString;
										}),
									};
								})}
								onChange={(values) => {
									// Filter out locked values and convert back to FilterOptions
									const unlockedValues = values.filter((v) => !v.isFixed);
									const newUnlockedFilterOptions = unlockedValues.map((v) => {
										const option = config.options?.find(
											(opt) => opt.id.toString() === v.value,
										);
										return option || { id: v.value, label: v.label };
									});
									// Combine locked values with new unlocked values
									const allValues = [
										...lockedValues,
										...newUnlockedFilterOptions,
									];
									onValuesChange(allValues);
								}}
								placeholder={
									config.placeholder ? t(config.placeholder) : undefined
								}
								isDisabled={disabled || isModalOpen}
								isSearchable={config.searchable}
								size={size}
							/>
						</FormField>
					);
				}

			case 'radio':
				// Use BaseRadioSelect for filter contexts (horizontal layout, better styling)
				return (
					<BaseRadioSelect
						filterType={filterType}
						config={config}
						selectedValues={selectedValues}
						onValuesChange={onValuesChange}
						disabled={disabled}
						isModalOpen={isModalOpen}
						isPreset={isPreset}
						size={size}
						layout={layout}
					/>
				);

			case 'single-select':
				return (
					<FormField
						name={filterType}
						labelHidden={labelHidden}
						label={
							<div className="flex items-center gap-2">
								<span>{t(config.label)}</span>
								{isPreset && (
									<Icon
										icon={faLock}
										size={size === 'xs' ? 'xs' : 'sm'}
										className={colors.classes.textSecondary}
									/>
								)}
							</div>
						}
						required={config.required}
						size={size}
					>
						<div className={cn((isPreset || disabled) && 'opacity-60')}>
							<Select
								name={filterType}
								options={
									config.options?.map((opt) => ({
										value: opt.id.toString(),
										label: t(opt.label),
									})) || []
								}
								value={selectedValues[0]?.id.toString() ?? ''}
								onChange={(value) => {
									if (value === '' || value == null) {
										// Clear the filter - set to empty array
										onValuesChange([]);
									} else {
										const option = config.options?.find(
											(opt) => opt.id.toString() === value,
										);
										if (option) {
											onValuesChange([option]);
										} else {
											onValuesChange([]);
										}
									}
								}}
								placeholder={
									config.placeholder ? t(config.placeholder) : undefined
								}
								disabled={disabled || isModalOpen || isPreset}
								allowClear={config.allowClear}
								size={size}
							/>
						</div>
					</FormField>
				);

			case 'date-range':
				return (
					<BaseDateRange
						filterType={filterType}
						config={config}
						selectedValues={selectedValues}
						onValuesChange={onValuesChange}
						disabled={disabled}
						isModalOpen={isModalOpen}
						lockedValues={lockedValues}
						size={size}
					/>
				);

			case 'date':
				// Extract current date from selected values
				const firstSelectedDate = selectedValues[0]?.data;
				const currentDate = firstSelectedDate
					? new Date(`${firstSelectedDate}T12:00:00`)
					: null;

				// Calculate date constraints based on other filters
				let minDate: Date | undefined;
				let maxDate: Date | undefined;

				if (
					filterType === 'orderDateTo' &&
					(otherFilters['orderDateFrom']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue = otherFilters['orderDateFrom']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'orderDateFrom' &&
					(otherFilters['orderDateTo']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['orderDateTo']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Invoice date constraints
				if (
					filterType === 'invoiceDateTo' &&
					(otherFilters['invoiceDateFrom']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue = otherFilters['invoiceDateFrom']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'invoiceDateFrom' &&
					(otherFilters['invoiceDateTo']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['invoiceDateTo']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Quote date constraints
				if (
					filterType === 'quoteDateTo' &&
					(otherFilters['quoteDateFrom']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue = otherFilters['quoteDateFrom']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'quoteDateFrom' &&
					(otherFilters['quoteDateTo']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['quoteDateTo']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Access logs date constraints
				if (
					filterType === 'apiAccessLogsToDate' &&
					(otherFilters['apiAccessLogsFromDate']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue =
						otherFilters['apiAccessLogsFromDate']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'apiAccessLogsFromDate' &&
					(otherFilters['apiAccessLogsToDate']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['apiAccessLogsToDate']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Login history date constraints
				if (
					filterType === 'loginHistoryToDate' &&
					(otherFilters['loginHistoryFromDate']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue =
						otherFilters['loginHistoryFromDate']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'loginHistoryFromDate' &&
					(otherFilters['loginHistoryToDate']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['loginHistoryToDate']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Client notes date constraints
				if (
					filterType === 'noteDateTo' &&
					(otherFilters['noteDateFrom']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue = otherFilters['noteDateFrom']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'noteDateFrom' &&
					(otherFilters['noteDateTo']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['noteDateTo']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				// Client visits date constraints
				if (
					filterType === 'visitDateTo' &&
					(otherFilters['visitDateFrom']?.length ?? 0) > 0
				) {
					// End date cannot be before start date
					const startDateValue = otherFilters['visitDateFrom']?.[0]?.data;
					if (startDateValue) {
						minDate = new Date(`${startDateValue}T00:00:00`);
					}
				}

				if (
					filterType === 'visitDateFrom' &&
					(otherFilters['visitDateTo']?.length ?? 0) > 0
				) {
					// Start date cannot be after end date
					const endDateValue = otherFilters['visitDateTo']?.[0]?.data;
					if (endDateValue) {
						maxDate = new Date(`${endDateValue}T23:59:59`);
					}
				}

				return (
					<FormField
						name={filterType}
						label={t(config.label)}
						required={config.required}
						size={size}
					>
						<DatePicker
							mode="single"
							selected={currentDate}
							onChange={(date) => {
								if (date) {
									const formattedDate = formatDateForAPI(date);
									onValuesChange([
										{
											id: formattedDate,
											label: formattedDate,
											data: formattedDate,
										},
									]);
								} else {
									onValuesChange([]);
								}
							}}
							placeholderText={
								config.placeholder ? t(config.placeholder) : t('Select date')
							}
							disabled={disabled || isModalOpen || lockedValues.length > 0}
							minDate={minDate}
							maxDate={maxDate}
							fullWidth={true}
							size={size}
							allowClear={config.allowClear}
						/>
					</FormField>
				);

			case 'text':
				return (
					<BaseTextInput
						filterType={filterType}
						config={config}
						selectedValues={selectedValues}
						onValuesChange={onValuesChange}
						disabled={disabled}
						isModalOpen={isModalOpen}
						lockedValues={lockedValues}
						debounceMs={300}
						size={size}
						layout={layout}
					/>
				);

			case 'proximity':
				return (
					<BaseProximityFilter
						filterType={filterType}
						config={config}
						selectedValues={selectedValues}
						onValuesChange={onValuesChange}
						disabled={disabled}
						isModalOpen={isModalOpen}
						isPreset={isPreset}
						size={size}
						layout={layout}
					/>
				);

			case 'number-range':
				return (
					<BaseNumberRange
						filterType={filterType}
						config={config}
						selectedValues={selectedValues}
						onValuesChange={onValuesChange}
						disabled={disabled}
						isModalOpen={isModalOpen}
						lockedValues={lockedValues}
						size={size}
					/>
				);

			default:
				console.warn(
					`[BaseFilter] Unknown filter type: ${config.type} for ${filterType}`,
				);
				return (
					<div
						className={cn(
							'p-2 text-warning text-xs',
							colors.classes.calloutWarning,
						)}
					>
						Warning: Unknown filter type "{config.type}" for {filterType}
					</div>
				);
		}
	} catch (error) {
		console.error('[BaseFilter] Error during component rendering:', error);
		console.error('[BaseFilter] Component state:', {
			filterType,
			config,
			selectedValues,
			lockedValues,
			disabled,
			isModalOpen,
			layout,
			otherFilters,
		});

		// Return a visible error component instead of crashing
		return (
			<div
				className={cn(
					'p-2 rounded text-danger text-xs',
					colors.classes.calloutDanger,
				)}
			>
				<div className="font-semibold">BaseFilter Error ({filterType}):</div>
				<div>{error instanceof Error ? error.message : String(error)}</div>
				<div className="mt-1 text-xs opacity-75">
					Check console for full details
				</div>
			</div>
		);
	}
};
